import React, { useState } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LayoutDashboard, LogOut, ChartBar, Car, Shield, Menu, X } from 'lucide-react';
import RealEstateAnalytics from '../components/RealEstateAnalytics';
import TaxiFleetAnalytics from '../components/TaxiFleetAnalytics';
import MHIntelligence from '../components/MHIntelligence';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Overview' },
    { path: '/dashboard/analytics', icon: ChartBar, label: 'Real Estate' },
    { path: '/dashboard/taxi', icon: Car, label: 'Taxi Fleet' },
    { path: '/dashboard/intelligence', icon: Shield, label: 'MH Intelligence' }
  ];

  const handleNavClick = () => {
    setSidebarOpen(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B1437] via-[#1a2556] to-[#0B1437] flex">
      {/* Mobile header bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-[#0B1437]/90 backdrop-blur-xl px-4 py-3 flex items-center justify-between">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="text-white p-1.5"
          data-testid="dashboard-menu-toggle"
        >
          {sidebarOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
        <Link to="/">
          <img
            src="https://customer-assets.emergentagent.com/job_realestateanalytics/artifacts/lhvv6y36_Menelaus%20Holding.png"
            alt="Menelaus"
            className="w-8 h-8"
          />
        </Link>
        <p className="text-slate-300 text-sm font-medium truncate max-w-[120px]">{user?.name}</p>
      </div>

      {/* Sidebar overlay for mobile */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/50"
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-overlay"
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed lg:sticky top-0 left-0 z-50 h-screen w-64 bg-[#0B1437]/95 lg:bg-[#0B1437]/80 backdrop-blur-xl border-r border-white/10 flex flex-col
        transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `} data-testid="dashboard-sidebar">
        <div className="p-6 border-b border-white/10 flex-shrink-0">
          <Link to="/" onClick={handleNavClick}>
            <img
              src="https://customer-assets.emergentagent.com/job_realestateanalytics/artifacts/lhvv6y36_Menelaus%20Holding.png"
              alt="Menelaus"
              className="w-16 h-16 lg:w-20 lg:h-20 mx-auto mb-3 hover:opacity-80 transition-opacity cursor-pointer"
            />
          </Link>
          <p className="text-slate-300 text-center text-sm font-medium">{user?.name}</p>
        </div>

        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={handleNavClick}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                  isActive
                    ? 'bg-gradient-to-r from-[#FF6B35] to-[#F7931E] text-white shadow-lg'
                    : 'text-slate-300 hover:bg-white/5'
                }`}
                style={isActive ? { boxShadow: '0 0 20px rgba(255, 107, 53, 0.3)' } : {}}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Icon size={20} />
                <span className="font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-white/10 flex-shrink-0">
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 text-slate-300 hover:bg-red-500/10 hover:text-red-400 rounded-xl transition-all duration-200"
            data-testid="logout-btn"
          >
            <LogOut size={20} />
            <span className="font-medium">Logout</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto pt-14 lg:pt-0">
        <Routes>
          <Route path="/" element={
            <div className="p-4 sm:p-8">
              <div className="max-w-4xl mx-auto">
                <div className="bg-[#1a2556]/60 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-5 sm:p-8 border border-white/10">
                  <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3 sm:mb-4">Welcome to Menelaus Holding</h1>
                  <p className="text-slate-300 text-sm sm:text-base lg:text-lg">
                    Your investment management platform. Navigate to Real Estate Analytics or Taxi Fleet to start calculating.
                  </p>
                </div>
              </div>
            </div>
          } />
          <Route path="/analytics" element={<RealEstateAnalytics />} />
          <Route path="/taxi" element={<TaxiFleetAnalytics />} />
          <Route path="/intelligence" element={<MHIntelligence />} />
        </Routes>
      </div>
    </div>
  );
};

export default Dashboard;
